// ============================================================================
// BrightPath - Notification Store (Zustand)
// BP-12-03: Notifications Frontend
// ============================================================================

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  Notification,
  NotificationCategory,
  NotificationFilterState,
  ToastNotification,
  UnreadCountResponse,
  GlobalNotificationSettings,
  NotificationPreference,
} from '../types/notifications';

// ----------------------------------------------------------------------------
// Store State Interface
// ----------------------------------------------------------------------------

interface NotificationState {
  // Notifications list
  notifications: Notification[];
  isLoading: boolean;
  error: string | null;
  
  // Pagination
  page: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  hasMore: boolean;
  
  // Unread counts
  unreadCount: number;
  unreadByCategory: Record<NotificationCategory, number>;
  
  // Filters
  filters: NotificationFilterState;
  
  // Toast notifications (real-time)
  toasts: ToastNotification[];
  
  // Notification center state
  isNotificationCenterOpen: boolean;
  
  // Preferences
  preferences: NotificationPreference[];
  globalSettings: GlobalNotificationSettings | null;
  
  // Push notification state
  pushEnabled: boolean;
  pushPermissionStatus: NotificationPermission | null;
  
  // WebSocket connection state
  wsConnected: boolean;
  wsReconnecting: boolean;
}

interface NotificationActions {
  // Notification management
  setNotifications: (notifications: Notification[]) => void;
  addNotification: (notification: Notification) => void;
  updateNotification: (id: string, updates: Partial<Notification>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  
  // Loading state
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  // Pagination
  setPage: (page: number) => void;
  setPagination: (data: { page: number; pageSize: number; totalPages: number; totalCount: number }) => void;
  appendNotifications: (notifications: Notification[]) => void;
  
  // Unread counts
  setUnreadCount: (count: number) => void;
  setUnreadByCategory: (counts: Record<NotificationCategory, number>) => void;
  decrementUnreadCount: () => void;
  incrementUnreadCount: () => void;
  
  // Read status
  markAsRead: (id: string) => void;
  markMultipleAsRead: (ids: string[]) => void;
  markAllAsRead: () => void;
  
  // Filters
  setFilters: (filters: Partial<NotificationFilterState>) => void;
  resetFilters: () => void;
  
  // Toasts
  addToast: (notification: Notification, duration?: number) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
  
  // Notification center
  openNotificationCenter: () => void;
  closeNotificationCenter: () => void;
  toggleNotificationCenter: () => void;
  
  // Preferences
  setPreferences: (preferences: NotificationPreference[]) => void;
  setGlobalSettings: (settings: GlobalNotificationSettings) => void;
  updatePreference: (category: NotificationCategory, updates: Partial<NotificationPreference>) => void;
  
  // Push notifications
  setPushEnabled: (enabled: boolean) => void;
  setPushPermissionStatus: (status: NotificationPermission) => void;
  
  // WebSocket
  setWsConnected: (connected: boolean) => void;
  setWsReconnecting: (reconnecting: boolean) => void;
  
  // Reset
  reset: () => void;
}

// ----------------------------------------------------------------------------
// Initial State
// ----------------------------------------------------------------------------

const initialFilters: NotificationFilterState = {
  category: 'all',
  isRead: 'all',
  priority: 'all',
  dateRange: 'all',
};

const initialState: NotificationState = {
  notifications: [],
  isLoading: false,
  error: null,
  page: 1,
  pageSize: 20,
  totalPages: 1,
  totalCount: 0,
  hasMore: false,
  unreadCount: 0,
  unreadByCategory: {} as Record<NotificationCategory, number>,
  filters: initialFilters,
  toasts: [],
  isNotificationCenterOpen: false,
  preferences: [],
  globalSettings: null,
  pushEnabled: false,
  pushPermissionStatus: null,
  wsConnected: false,
  wsReconnecting: false,
};

// ----------------------------------------------------------------------------
// Store Creation
// ----------------------------------------------------------------------------

export const useNotificationStore = create<NotificationState & NotificationActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // Notification management
        setNotifications: (notifications) =>
          set({ notifications }, false, 'setNotifications'),

        addNotification: (notification) =>
          set(
            (state) => ({
              notifications: [notification, ...state.notifications],
              totalCount: state.totalCount + 1,
            }),
            false,
            'addNotification'
          ),

        updateNotification: (id, updates) =>
          set(
            (state) => ({
              notifications: state.notifications.map((n) =>
                n.id === id ? { ...n, ...updates } : n
              ),
            }),
            false,
            'updateNotification'
          ),

        removeNotification: (id) =>
          set(
            (state) => ({
              notifications: state.notifications.filter((n) => n.id !== id),
              totalCount: Math.max(0, state.totalCount - 1),
            }),
            false,
            'removeNotification'
          ),

        clearNotifications: () =>
          set({ notifications: [], totalCount: 0, page: 1 }, false, 'clearNotifications'),

        // Loading state
        setLoading: (isLoading) => set({ isLoading }, false, 'setLoading'),
        setError: (error) => set({ error }, false, 'setError'),

        // Pagination
        setPage: (page) => set({ page }, false, 'setPage'),

        setPagination: ({ page, pageSize, totalPages, totalCount }) =>
          set(
            {
              page,
              pageSize,
              totalPages,
              totalCount,
              hasMore: page < totalPages,
            },
            false,
            'setPagination'
          ),

        appendNotifications: (notifications) =>
          set(
            (state) => ({
              notifications: [...state.notifications, ...notifications],
            }),
            false,
            'appendNotifications'
          ),

        // Unread counts
        setUnreadCount: (unreadCount) =>
          set({ unreadCount }, false, 'setUnreadCount'),

        setUnreadByCategory: (unreadByCategory) =>
          set({ unreadByCategory }, false, 'setUnreadByCategory'),

        decrementUnreadCount: () =>
          set(
            (state) => ({
              unreadCount: Math.max(0, state.unreadCount - 1),
            }),
            false,
            'decrementUnreadCount'
          ),

        incrementUnreadCount: () =>
          set(
            (state) => ({
              unreadCount: state.unreadCount + 1,
            }),
            false,
            'incrementUnreadCount'
          ),

        // Read status
        markAsRead: (id) => {
          const state = get();
          const notification = state.notifications.find((n) => n.id === id);
          
          if (notification && !notification.isRead) {
            set(
              (state) => ({
                notifications: state.notifications.map((n) =>
                  n.id === id
                    ? { ...n, isRead: true, readAt: new Date().toISOString() }
                    : n
                ),
                unreadCount: Math.max(0, state.unreadCount - 1),
              }),
              false,
              'markAsRead'
            );
          }
        },

        markMultipleAsRead: (ids) =>
          set(
            (state) => {
              let decrementCount = 0;
              const notifications = state.notifications.map((n) => {
                if (ids.includes(n.id) && !n.isRead) {
                  decrementCount++;
                  return { ...n, isRead: true, readAt: new Date().toISOString() };
                }
                return n;
              });

              return {
                notifications,
                unreadCount: Math.max(0, state.unreadCount - decrementCount),
              };
            },
            false,
            'markMultipleAsRead'
          ),

        markAllAsRead: () =>
          set(
            (state) => ({
              notifications: state.notifications.map((n) =>
                n.isRead ? n : { ...n, isRead: true, readAt: new Date().toISOString() }
              ),
              unreadCount: 0,
            }),
            false,
            'markAllAsRead'
          ),

        // Filters
        setFilters: (filters) =>
          set(
            (state) => ({
              filters: { ...state.filters, ...filters },
              page: 1, // Reset to first page when filters change
            }),
            false,
            'setFilters'
          ),

        resetFilters: () =>
          set({ filters: initialFilters, page: 1 }, false, 'resetFilters'),

        // Toasts
        addToast: (notification, duration = 5000) => {
          const toastId = `toast-${notification.id}-${Date.now()}`;
          const toast: ToastNotification = {
            id: toastId,
            notification,
            isVisible: true,
            duration,
          };

          set(
            (state) => ({
              toasts: [...state.toasts, toast],
            }),
            false,
            'addToast'
          );

          // Auto-remove after duration
          if (duration > 0) {
            setTimeout(() => {
              get().removeToast(toastId);
            }, duration);
          }
        },

        removeToast: (id) =>
          set(
            (state) => ({
              toasts: state.toasts.filter((t) => t.id !== id),
            }),
            false,
            'removeToast'
          ),

        clearToasts: () => set({ toasts: [] }, false, 'clearToasts'),

        // Notification center
        openNotificationCenter: () =>
          set({ isNotificationCenterOpen: true }, false, 'openNotificationCenter'),

        closeNotificationCenter: () =>
          set({ isNotificationCenterOpen: false }, false, 'closeNotificationCenter'),

        toggleNotificationCenter: () =>
          set(
            (state) => ({
              isNotificationCenterOpen: !state.isNotificationCenterOpen,
            }),
            false,
            'toggleNotificationCenter'
          ),

        // Preferences
        setPreferences: (preferences) =>
          set({ preferences }, false, 'setPreferences'),

        setGlobalSettings: (globalSettings) =>
          set({ globalSettings }, false, 'setGlobalSettings'),

        updatePreference: (category, updates) =>
          set(
            (state) => ({
              preferences: state.preferences.map((p) =>
                p.category === category ? { ...p, ...updates } : p
              ),
            }),
            false,
            'updatePreference'
          ),

        // Push notifications
        setPushEnabled: (pushEnabled) =>
          set({ pushEnabled }, false, 'setPushEnabled'),

        setPushPermissionStatus: (pushPermissionStatus) =>
          set({ pushPermissionStatus }, false, 'setPushPermissionStatus'),

        // WebSocket
        setWsConnected: (wsConnected) =>
          set({ wsConnected }, false, 'setWsConnected'),

        setWsReconnecting: (wsReconnecting) =>
          set({ wsReconnecting }, false, 'setWsReconnecting'),

        // Reset
        reset: () => set(initialState, false, 'reset'),
      }),
      {
        name: 'brightpath-notifications',
        partialize: (state) => ({
          // Only persist these fields
          pushEnabled: state.pushEnabled,
          filters: state.filters,
        }),
      }
    ),
    { name: 'NotificationStore' }
  )
);

// ----------------------------------------------------------------------------
// Selectors
// ----------------------------------------------------------------------------

export const selectUnreadNotifications = (state: NotificationState) =>
  state.notifications.filter((n) => !n.isRead);

export const selectNotificationsByCategory = (
  state: NotificationState,
  category: NotificationCategory
) => state.notifications.filter((n) => n.category === category);

export const selectRecentNotifications = (
  state: NotificationState,
  count: number = 5
) => state.notifications.slice(0, count);

export const selectHasUnread = (state: NotificationState) =>
  state.unreadCount > 0;

export default useNotificationStore;
